/**
 * Wishlist Items Block Frontend JavaScript
 * Handles synchronization with wishlist changes
 */

(function () {
  "use strict";

  const WishlistItemsSync = {
    init: function () {
      this.bindEvents();
    },

    /**
     * Bind event listeners for wishlist updates
     */
    bindEvents: function () {
      window.addEventListener(
        "brandy-update-wishlist",
        this.handleWishlistUpdate.bind(this)
      );

      window.addEventListener(
        "brandy-update-wishlist-items",
        this.handleWishlistUpdate.bind(this)
      );
    },

    /**
     * Handle wishlist updates (add/remove from wishlist)
     * @param {CustomEvent} event - The wishlist update event
     */
    handleWishlistUpdate: function (event) {
      const { productId, action, wishlistData } = event.detail;

      // Always refresh wishlist items blocks when an update occurs
      // If we have wishlist data from the AJAX response, use it to update button states
      if (wishlistData) {
        this.updateWishlistItemsBlocksWithData(wishlistData, action);
      } else {
        // Fallback: refresh blocks without wishlist data
        // This ensures blocks are updated even if wishlistData is missing
        if (this.refreshTimeout) {
          clearTimeout(this.refreshTimeout);
        }

        this.refreshTimeout = setTimeout(() => {
          this.refreshWishlistItemsBlocks();
        }, 300);
      }
    },

    /**
     * Refresh all wishlist items blocks on the page
     */
    refreshWishlistItemsBlocks: function () {
      const wishlistItemsBlocks = this.findWishlistItemsBlocks();

      if (wishlistItemsBlocks.length === 0) {
        return;
      }

      wishlistItemsBlocks.forEach((block) => {
        this.performBlockRefresh(block);
      });
    },

    updateWishlistItemsBlocksWithData: function (wishlistData, action) {
      // Look for wishlist items blocks using multiple methods
      const wishlistItemsBlocks = this.findWishlistItemsBlocks();

      if (wishlistItemsBlocks.length === 0) {
        return;
      }

      // Always update button states based on the wishlist data
      this.updateAddToWishlistButtons(wishlistData);

      // Refresh WishlistItems blocks for both add and remove actions
      // since they are WordPress Query blocks that depend on the wishlist
      // Use a debounced refresh to avoid too many calls
      if (this.refreshTimeout) {
        clearTimeout(this.refreshTimeout);
      }

      this.refreshTimeout = setTimeout(() => {
        this.refreshWishlistItemsBlocks();
      }, 300); // Slightly longer delay to batch multiple rapid updates
    },

    updateAddToWishlistButtons: function (wishlistData) {
      if (!wishlistData || !wishlistData.products) {
        return;
      }

      // Get all product IDs currently in the wishlist
      const wishlistProductIds = wishlistData.products.map((product) =>
        product.id.toString()
      );

      // Find all Add to Wishlist buttons on the page
      const addToWishlistButtons = document.querySelectorAll(
        '[data-block-name="brandy/add-to-wishlist"]'
      );

      addToWishlistButtons.forEach((button) => {
        const productId = button.getAttribute("data-product-id");
        if (!productId) return;

        const isInWishlist = wishlistProductIds.includes(productId);

        if (isInWishlist) {
          button.classList.add("added");
        } else {
          button.classList.remove("added");
        }
      });
    },

    /**
     * Find all wishlist items blocks on the page
     * @returns {Array<HTMLElement>} Array of wishlist items block elements
     */
    findWishlistItemsBlocks: function () {
      const blocks = [];

      const contextBlocks = document.querySelectorAll(
        ".wp-block-brandy-wishlist-items"
      );

      contextBlocks.forEach((block) => {
        blocks.push(block);
      });

      return blocks;
    },

    /**
     * Perform the actual block refresh
     * @param {HTMLElement} block - The block element to refresh
     */
    performBlockRefresh: function (block) {
      if (block.classList.contains("wishlist-items-loading")) {
        return;
      }

      block.classList.add("wishlist-items-loading");

      const originalContent = block.innerHTML;

      const loadingIndicator = document.createElement("div");
      loadingIndicator.className = "wishlist-items-loading-indicator";
      loadingIndicator.innerHTML =
        '<div class="loading-spinner">Refreshing...</div>';

      // Insert loading indicator before the block content
      block.insertBefore(loadingIndicator, block.firstChild);

      this.refreshBlockViaAjax(block, loadingIndicator, originalContent);
    },

    /**
     * Refresh block content via AJAX
     * @param {HTMLElement} block - The block element
     * @param {HTMLElement} loadingIndicator - The loading indicator element
     * @param {string} originalContent - Original content for fallback
     */
    refreshBlockViaAjax: function (block, loadingIndicator, originalContent) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set(
        "brandy_refresh_wishlist",
        Date.now().toString()
      );

      fetch(currentUrl.toString(), {
        method: "GET",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
        cache: "no-cache",
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");

          const blockQueryId = block.getAttribute("data-query-id");
          const blockName = block.getAttribute("data-block-name");
          const blockType = block.getAttribute("data-type");
          let refreshedBlock = null;

          if (blockQueryId) {
            refreshedBlock = doc.querySelector(
              `.wp-block-query[data-query-id="${blockQueryId}"]`
            );
          }

          if (!refreshedBlock && blockName) {
            refreshedBlock = doc.querySelector(
              `[data-block-name="${blockName}"]`
            );
          }

          if (!refreshedBlock && blockType) {
            refreshedBlock = doc.querySelector(
              `.wp-block[data-type="${blockType}"]`
            );
          }

          if (!refreshedBlock) {
            const classSelectors = block.className
              .split(" ")
              .map((cls) => `.${cls}`)
              .join("");
            if (classSelectors) {
              refreshedBlock = doc.querySelector(classSelectors);
            }
          }

          if (!refreshedBlock) {
            refreshedBlock = this.findMatchingBlockInDocument(doc, block);
          }

          if (refreshedBlock) {
            block.innerHTML = refreshedBlock.innerHTML;

            this.reinitializeBlockContent(block);
          }
        })
        .catch((error) => {
          console.error(
            "WishlistItemsSync: Error refreshing wishlist items block:",
            error
          );
        })
        .finally(() => {
          block.classList.remove("wishlist-items-loading");
          if (loadingIndicator && loadingIndicator.parentNode) {
            loadingIndicator.remove();
          }
        });
    },

    /**
     * Find matching block in the refreshed document
     * @param {Document} doc - The parsed document
     * @param {HTMLElement} originalBlock - The original block element
     * @returns {HTMLElement|null} The matching block or null
     */
    findMatchingBlockInDocument: function (doc, originalBlock) {
      const queryId = originalBlock.getAttribute("data-query-id");
      if (queryId) {
        const block = doc.querySelector(
          `.wp-block-query[data-query-id="${queryId}"]`
        );
        if (block) return block;
      }

      const allQueryBlocks = doc.querySelectorAll(".wp-block-query");
      if (allQueryBlocks.length > 0) {
        return allQueryBlocks[0];
      }

      return null;
    },

    /**
     * Re-initialize interactive elements in the refreshed block content
     * @param {HTMLElement} block - The refreshed block element
     */
    reinitializeBlockContent: function (block) {
      if (window.jQuery) {
        window.jQuery(block).trigger("brandy-wishlist-items-refreshed");
      }

      block.dispatchEvent(
        new CustomEvent("brandy-wishlist-items-refreshed", {
          detail: { block: block },
        })
      );
    },
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      WishlistItemsSync.init();
    });
  } else {
    WishlistItemsSync.init();
  }
  window.WishlistItemsSync = WishlistItemsSync;
})();
